import BigNumber from "bignumber.js";
import { ReactElement } from "react";
import { useTranslation } from "@pancakeswap/localization";
import { getBalanceNumber } from "@pancakeswap/utils/formatBalance";
import { DeserializedPool } from "./types";
import NotEnoughTokensModal from "../Modal/NotEnoughTokensModal";
import styled from "styled-components";
import { Button } from "../../components/Button";
import { Text } from "../../components/Text";
import { Box, Flex } from "../../components/Box";
import { Balance } from "../../components/Balance";
import { Skeleton } from "../../components/Skeleton";
import { useModal } from "../Modal";
import { useTooltip } from "../../hooks/useTooltip";
// import { MinusIcon, AddIcon } from "../../components/Svg";
import { FlexGap } from "../../components";

const IconButton = styled(Button)`
  padding: 0;
  background:transparent;
`;


interface StakeActionsPropsType<T> {
  pool: DeserializedPool<T>;
  stakingTokenBalance: BigNumber;
  stakedBalance: BigNumber;
  isBnbPool: boolean;
  isStaked: ConstrainBoolean;
  isLoading?: boolean;
  hideLocateAddress?: boolean;
}

export interface StakeModalPropsType<T> {
  isBnbPool: boolean;
  pool: DeserializedPool<T>;
  stakingTokenBalance: BigNumber;
  stakingTokenPrice: number;
  isRemovingStake?: boolean;
  onDismiss?: () => void;
}

export function withStakeActions<T>(StakeModal: (props: StakeModalPropsType<T>) => ReactElement) {
  return ({
    pool,
    stakingTokenBalance,
    stakedBalance,
    isBnbPool,
    isStaked,
    isLoading = false,
    hideLocateAddress = false,
  }: StakeActionsPropsType<T>) => {
    const { stakingToken, stakingTokenPrice, stakingLimit, isFinished, userData } = pool;
    const { t } = useTranslation();
    const stakedTokenBalance = getBalanceNumber(stakedBalance, stakingToken?.decimals);
    const stakedTokenDollarBalance = stakingTokenPrice
      ? getBalanceNumber(stakedBalance?.multipliedBy(stakingTokenPrice), stakingToken?.decimals)
      : 0;

    const [onPresentTokenRequired] = useModal(
      <NotEnoughTokensModal
        hideLocateAddress={hideLocateAddress}
        tokenAddress={stakingToken.address}
        tokenSymbol={stakingToken?.symbol || ""}
      />
    );

    const [onPresentStake] = useModal(
      <StakeModal
        isBnbPool={isBnbPool}
        pool={pool}
        stakingTokenBalance={stakingTokenBalance}
        stakingTokenPrice={stakingTokenPrice || 0}
      />
    );

    const [onPresentUnstake] = useModal(
      <StakeModal
        stakingTokenBalance={stakingTokenBalance}
        isBnbPool={isBnbPool}
        pool={pool}
        stakingTokenPrice={stakingTokenPrice || 0}
        isRemovingStake
      />
    );

    const { targetRef, tooltip, tooltipVisible } = useTooltip(
      t("You’ve already staked the maximum amount you can stake in this pool!"),
      { placement: "bottom" }
    );

    const reachStakingLimit = stakingLimit?.gt(0) && userData?.stakedBalance?.gte(stakingLimit);

    const renderStakeAction = () => {
      return isStaked ? (
        <Box>
          <Flex justifyContent="space-between" alignItems="center">
            <Flex flexDirection="column">
              <>
                <Balance bold fontSize="20px" decimals={2} value={stakedTokenBalance} />
                <Text fontSize="12px" color="textSubtle">
                  <Balance
                    fontSize="12px"
                    color="textSubtle"
                    decimals={2}
                    value={stakedTokenDollarBalance}
                    prefix="~"
                    unit=" USD"
                  />
                </Text>
              </>
            </Flex>
          </Flex>
          <FlexGap flex="1 1 0px" gap="8px" mt="8px">
            <IconButton
              width="100%"
              variant="secondary"
              onClick={() => {
                onPresentUnstake()
              }}
            >
              {/* <MinusIcon color="primary" width="24px" /> */}
              Pull out
            </IconButton>
            {reachStakingLimit ? (
              <span ref={targetRef}>
                <IconButton variant="secondary" width="100%" disabled>
                  {/* <AddIcon color="textDisabled" width="24px" height="24px" /> */}
                  Deposit
                </IconButton>
              </span>
            ) : (
              <IconButton 
                variant="secondary" 
                width="100%"
                onClick={stakingTokenBalance.gt(0) ? onPresentStake : onPresentTokenRequired}
                disabled={isFinished}
              >
                {/* <AddIcon color="primary" width="24px" height="24px" /> */}
                Insert
              </IconButton>
            )}
          </FlexGap>
          {tooltipVisible && tooltip}
        </Box>
      ) : (
        <Button disabled={isFinished} mt="8px" onClick={stakingTokenBalance.gt(0) ? onPresentStake : onPresentTokenRequired}>
          {t("Stake")}
        </Button>
      );
    };

    return (
      <Flex flexDirection="column">{isLoading ? <Skeleton width="100%" height="52px" /> : renderStakeAction()}</Flex>
    );
  };
}
